(() => {
  const CART_EVENT = 'cart:update';
  const STICKY_SELECTOR = 'sticky-add-to-cart .sticky-add-to-cart__price';
  const MAIN_PRICE_SELECTOR = 'product-price [ref="priceContainer"]';
  const VAT = Number(window.RoyseProfessionalPricing?.vatRate || 0.21);
  const TAXES_INCLUDED = window.RoyseProfessionalPricing?.taxesIncluded !== false;
  const IS_PRO = Boolean(window.RoyseProfessionalPricing?.isProfessional);
  let cartSyncTimer = null;
  let stickySyncQueued = false;
  let pricePassTimer = null;

  const money = (cents, currency) => {
    const locale = document.documentElement.lang || 'es-ES';
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency || window.RoyseProfessionalPricing?.currency || 'EUR',
      }).format((Number(cents) || 0) / 100);
    } catch (_) {
      return ((Number(cents) || 0) / 100).toFixed(2) + ' €';
    }
  };

  const netFromGrossCents = (grossCents) => Math.round((Number(grossCents) || 0) / (1 + VAT));

  const parseMoneyText = (text) => {
    if (!text) return null;
    const normalized = text.replace(/\u00a0/g, ' ').trim();
    const match = normalized.match(/(-?\d{1,3}(?:\.\d{3})*(?:,\d{1,2})|-?\d+(?:[.,]\d{1,2})?)/);
    if (!match) return null;
    let raw = match[1];
    if (raw.includes(',') && raw.includes('.')) raw = raw.replace(/\./g, '').replace(',', '.');
    else if (raw.includes(',')) raw = raw.replace(',', '.');
    const value = Number(raw);
    if (!Number.isFinite(value)) return null;
    return Math.round(value * 100);
  };

  const isSingleDisplayedPrice = (node) => {
    if (!(node instanceof Element)) return false;
    if (node.closest('.compare-at-price')) return false;
    const text = node.textContent || '';
    const matches = text.match(/(?:€\s*-?\d[\d.,]*|-?\d[\d.,]*\s*€)/g) || [];
    return matches.length === 1;
  };

  const renderProfessionalPrice = (node) => {
    if (!IS_PRO || !(node instanceof Element)) return;
    if (node.querySelector('.royse-price-net') || node.closest('.royse-price-net, .royse-price-gross')) return;
    if (!isSingleDisplayedPrice(node)) return;

    const grossCents = parseMoneyText(node.textContent);
    if (grossCents === null) return;
    const taxable = node.closest('[data-royse-taxable]')?.dataset.royseTaxable !== 'false';
    if (!taxable) return;
    const netCents = TAXES_INCLUDED ? netFromGrossCents(grossCents) : grossCents;
    const vatCents = TAXES_INCLUDED ? grossCents - netCents : Math.round(netCents * VAT);
    const currency = window.RoyseProfessionalPricing?.currency || 'EUR';

    node.setAttribute('data-royse-gross-cents', String(grossCents));
    node.innerHTML =
      '<span class="royse-price-net">' + money(netCents, currency) + '<span class="royse-price-net-label"> + IVA</span></span>' +
      '<span class="royse-price-gross">' + 'IVA: ' + money(vatCents, currency) + '</span>';
  };

  const renderMassyPrice = (host) => {
    const discounted = host.querySelector('.massy-custom-pricing-price-discount');
    const original = host.querySelector('.massy-custom-pricing-price');
    const source = discounted || original;
    if (!source) return;
    const gross = parseMoneyText(source.textContent);
    if (gross === null) return;
    let nativePrice = host.nextElementSibling;
    while (nativePrice && nativePrice.tagName.toLowerCase().startsWith('massy-')) nativePrice = nativePrice.nextElementSibling;
    const taxable = nativePrice?.querySelector('[data-royse-taxable]')?.dataset.royseTaxable !== 'false';
    const net = taxable && TAXES_INCLUDED ? netFromGrossCents(gross) : gross;
    const tax = taxable ? (TAXES_INCLUDED ? gross - net : Math.round(net * VAT)) : 0;
    const baseGross = discounted && original ? parseMoneyText(original.textContent) : null;
    const baseNet = baseGross === null ? null : (taxable && TAXES_INCLUDED ? netFromGrossCents(baseGross) : baseGross);
    const html = (baseNet !== null && baseNet !== net ? '<s class="royse-massy-base">' + money(baseNet) + '</s>' : '') +
      '<span class="royse-price-net">' + money(net) + (taxable ? '<span class="royse-price-net-label"> + IVA</span>' : '') + '</span>' +
      (taxable ? '<span class="royse-price-gross">IVA: ' + money(tax) + '</span>' : '');
    let output = host.querySelector(':scope > .royse-massy-net');
    if (!output) {
      output = document.createElement('span');
      output.className = 'royse-massy-net';
      host.appendChild(output);
    }
    if (output.innerHTML !== html) output.innerHTML = html;
    host.classList.add('royse-massy-ready');
  };

  const applyProfessionalNetPrices = () => {
    if (!IS_PRO) return;

    document.querySelectorAll('massy-custom-pricing').forEach(renderMassyPrice);

    const selectors = [
      'product-price .price',
      '.sticky-add-to-cart__price .price',
      '.resource-card .price',
      '.product-card .price',
      '.cart-items__unit-price-wrapper > div > span:not(.visually-hidden)',
      '.cart-items__price text-component',
      '[data-testid="cart-total-value"]',
      '[data-cart-subtotal]'
    ];

    document.querySelectorAll(selectors.join(',')).forEach(renderProfessionalPrice);
    queueStickySync();
  };

  const queueProfessionalPricePass = (delay = 80) => {
    if (!IS_PRO) return;
    clearTimeout(pricePassTimer);
    pricePassTimer = setTimeout(applyProfessionalNetPrices, delay);
  };

  const getMainVisiblePrice = () => {
    const nodes = Array.from(document.querySelectorAll(MAIN_PRICE_SELECTOR));
    return (
      nodes.find((node) => !node.closest('sticky-add-to-cart') && node.offsetParent !== null) ||
      nodes.find((node) => !node.closest('sticky-add-to-cart')) ||
      null
    );
  };

  const syncStickyPrice = () => {
    stickySyncQueued = false;
    const sticky = document.querySelector(STICKY_SELECTOR);
    const source = getMainVisiblePrice();
    if (!sticky || !source) return;

    if (source.hasAttribute('data-royse-taxable')) sticky.setAttribute('data-royse-taxable', source.getAttribute('data-royse-taxable'));
    const productPrice = source.closest('product-price');
    let massyPrice = productPrice?.previousElementSibling;
    while (massyPrice && massyPrice.tagName.toLowerCase().startsWith('massy-') && !massyPrice.matches('massy-custom-pricing')) massyPrice = massyPrice.previousElementSibling;
    const massyOutput = massyPrice?.matches('massy-custom-pricing') && massyPrice.querySelector('.royse-massy-net');
    const next = massyOutput ? (source.querySelector('.royse-retail')?.outerHTML || '') + massyOutput.outerHTML : source.innerHTML.trim();
    if (!next || sticky.innerHTML.trim() === next) return;
    sticky.innerHTML = next;
    queueProfessionalPricePass(20);
  };

  const queueStickySync = () => {
    if (stickySyncQueued) return;
    stickySyncQueued = true;
    requestAnimationFrame(() => requestAnimationFrame(syncStickyPrice));
  };

  const syncCartFromAjax = async () => {
    try {
      const response = await fetch('/cart.js', {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;

      const cart = await response.json();
      const currency = cart.currency || window.RoyseProfessionalPricing?.currency || 'EUR';

      for (const item of cart.items || []) {
        if (!item.key) continue;
        const escapedKey = window.CSS && CSS.escape ? CSS.escape(item.key) : item.key.replace(/"/g, '\\"');
        const rows = document.querySelectorAll('.cart-items__table-row[data-key="' + escapedKey + '"]');

        rows.forEach((row) => {
          const unitWrap = row.querySelector('.cart-items__unit-price-wrapper > div');
          const linePrice = row.querySelector('.cart-items__price text-component');

          if (unitWrap) {
            if (Number(item.final_price) !== Number(item.original_price)) {
              unitWrap.innerHTML =
                '<span class="visually-hidden">Precio de oferta</span>' +
                '<span>' + money(item.final_price, currency) + '</span>' +
                '<span class="visually-hidden">Precio habitual</span>' +
                '<s class="compare-at-price">' + money(IS_PRO && TAXES_INCLUDED && item.taxable !== false ? netFromGrossCents(item.original_price) : item.original_price, currency) + '</s>';
            } else {
              unitWrap.innerHTML = '<span>' + money(item.original_price, currency) + '</span>';
            }
          }

          if (linePrice) {
            const formatted = money(item.final_line_price, currency);
            linePrice.textContent = formatted;
            linePrice.setAttribute('value', formatted);
          }
        });
      }

      const formattedTotal = money(cart.total_price, currency);
      document.querySelectorAll('[data-testid="cart-total-value"], [data-cart-subtotal]').forEach((node) => {
        node.textContent = formattedTotal;
        if (node.hasAttribute('value')) node.setAttribute('value', formattedTotal);
      });

      queueProfessionalPricePass(20);
    } catch (_) {
      // La sincronización visual nunca debe bloquear la compra.
    }
  };

  const queueCartSync = (delay = 180) => {
    clearTimeout(cartSyncTimer);
    cartSyncTimer = setTimeout(syncCartFromAjax, delay);
  };

  const ensureStyles = () => {
    if (!IS_PRO || document.getElementById('royse-professional-price-styles')) return;
    const style = document.createElement('style');
    style.id = 'royse-professional-price-styles';
    style.textContent = [
      '.royse-price-net{display:block;font:inherit;line-height:1.15;}',
      '.royse-massy-ready .massy-custom-pricing-price-wrapper{display:none!important;}',
      '.royse-massy-base{display:block;font-size:.75em;opacity:.65;}',
      '.royse-price-net-label{font-size:.72em;font-weight:500;opacity:.8;white-space:nowrap;}',
      '.royse-price-gross{display:block;font-size:.72em;line-height:1.2;opacity:.7;margin-top:.15rem;white-space:nowrap;}',
      '.cart-items__price .royse-price-net,.cart-items__unit-price-wrapper .royse-price-net{display:inline-block;}',
      '.cart-items__price .royse-price-gross,.cart-items__unit-price-wrapper .royse-price-gross{font-size:.68em;}',
      '.sticky-add-to-cart__price .royse-price-gross{font-size:.65em;}'
    ].join('');
    document.head.appendChild(style);
  };

  document.addEventListener('royse:price-view', () => { queueStickySync(); queueProfessionalPricePass(0); });

  document.addEventListener(CART_EVENT, () => {
    queueCartSync(120);
    setTimeout(() => queueCartSync(0), 450);
    queueStickySync();
    queueProfessionalPricePass(550);
  });

  document.addEventListener('variant:update', () => {
    setTimeout(queueStickySync, 40);
    setTimeout(queueStickySync, 250);
    queueProfessionalPricePass(300);
  });

  const observer = new MutationObserver((mutations) => {
    let shouldSyncSticky = false;
    let drawerOpened = false;
    let shouldRefreshPrices = false;

    for (const mutation of mutations) {
      if (
        mutation.type === 'attributes' &&
        mutation.attributeName === 'open' &&
        mutation.target instanceof HTMLDialogElement &&
        mutation.target.closest('cart-drawer-component') &&
        mutation.target.open
      ) {
        drawerOpened = true;
      }

      const target = mutation.target instanceof Element ? mutation.target : mutation.target?.parentElement;
      if (
        target &&
        (
          target.matches?.(MAIN_PRICE_SELECTOR) ||
          target.closest?.('product-price')
        )
      ) {
        shouldSyncSticky = true;
      }

      if (target && !target.closest?.('.royse-price-net') && !target.closest?.('.royse-price-gross')) {
        shouldRefreshPrices = true;
      }
    }

    if (shouldSyncSticky) queueStickySync();
    if (drawerOpened) {
      queueCartSync(120);
      setTimeout(() => queueCartSync(0), 500);
    }
    if (shouldRefreshPrices) queueProfessionalPricePass(120);
  });

  observer.observe(document.documentElement, {
    subtree: true,
    childList: true,
    characterData: true,
    attributes: true,
    attributeFilter: ['open'],
  });

  const init = () => {
    ensureStyles();
    queueStickySync();
    queueCartSync(250);
    queueProfessionalPricePass(350);
  };

  window.addEventListener('pageshow', init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();


(() => {
  if (!window.RoyseProfessionalPricing?.isProfessional) return;
  const productId = document.querySelector('variant-picker[data-template-product-match="true"]')?.dataset.productId;
  if (!['10090632937800', '9898887905608', '10143298584904', '10223429157192'].includes(productId)) return;
  const picker = document.querySelector('variant-picker[data-product-id="' + productId + '"]');
  if (!picker || picker.dataset.templateProductMatch !== 'true') return;
  let initialId = '';
  try {
    initialId = String(JSON.parse(picker.querySelector('script[type="application/json"]')?.textContent || 'null')?.id || '');
  } catch (_) {}
  const scrollKey = 'royse-c6-variant-scroll';
  try {
    const saved = JSON.parse(sessionStorage.getItem(scrollKey) || 'null');
    sessionStorage.removeItem(scrollKey);
    if (saved?.path === location.pathname && saved?.variant === initialId && Number.isFinite(saved.y)) {
      requestAnimationFrame(() => window.scrollTo({ top: saved.y, behavior: 'instant' }));
    }
  } catch (_) {}
  let reloadTimer;
  document.addEventListener('variant:update', (event) => {
    if (String(event.detail?.data?.productId) !== productId) return;
    if (!(event.target instanceof Element) || !event.target.matches('variant-picker[data-template-product-match="true"]')) return;
    clearTimeout(reloadTimer);
    const variantId = String(event.detail?.resource?.id || '');
    if (!variantId || variantId === initialId) return;
    reloadTimer = setTimeout(() => {
      const next = new URL(location.href);
      next.searchParams.delete('option_values');
      next.searchParams.set('variant', variantId);
      try {
        sessionStorage.setItem(scrollKey, JSON.stringify({ path: next.pathname, variant: variantId, y: window.scrollY }));
      } catch (_) {}
      location.replace(next.href);
    }, 120);
  });
})();



